import { useAuth } from "@clerk/clerk-react";
import { Box, ButtonBase, IconButton, Typography } from "@mui/material";
import { getMarkRange } from "@tiptap/core";
import { Editor as TipTapEditor } from "@tiptap/react";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";

import { OrganizationTemplateRubricSuggestion } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";

import styles from "./styles";

interface HyperlinkSuggestionPopupProps {
  editor: TipTapEditor;
  suggestion: OrganizationTemplateRubricSuggestion;
  onApplied?: (suggestionId: number, beforeHtml: string) => void;
  onDismiss: () => void | Promise<void>;
}

const HyperlinkSuggestionPopup = ({
  editor,
  suggestion,
  onApplied,
  onDismiss,
}: HyperlinkSuggestionPopupProps) => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const [anchorText, setAnchorText] = useState(
    suggestion.proposedReplacement ?? "",
  );

  useEffect(() => {
    setAnchorText(suggestion.proposedReplacement ?? "");
  }, [suggestion.id, suggestion.proposedReplacement]);

  const handleApply = async () => {
    const html = anchorText.trim();
    if (!html || !currentOrg?.id) return;

    const highlightMark = editor.state.schema.marks.highlight;
    if (!highlightMark) return;
    const { from } = editor.state.selection;
    const $pos = editor.state.doc.resolve(from);
    const range = getMarkRange($pos, highlightMark);
    if (!range) return;

    const beforeHtml = editor.getHTML();

    editor
      .chain()
      .focus()
      .insertContentAt({ from: range.from, to: range.to }, html, {
        parseOptions: { preserveWhitespace: "full" },
      })
      .run();

    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg.id}/organization-template-rubric-suggestions/suggestion/${suggestion.id}`,
      "PATCH",
      {},
      { status: "applied" },
    );

    onApplied?.(suggestion.id, beforeHtml);
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.head}>
        <Box sx={styles.avatar}>
          <Box
            component="i"
            className="fa-solid fa-link"
            sx={styles.avatarIcon}
          />
        </Box>
        <Box sx={styles.helperBlock}>
          <Typography sx={styles.helperText}>Bento helper</Typography>
          <Box sx={styles.kindRow}>
            <Box component="span" sx={styles.kindDot} />
            Heads-up · Link formatting
          </Box>
        </Box>
        <IconButton size="small" sx={styles.closeButton} onClick={onDismiss}>
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={styles.closeIcon}
          />
        </IconButton>
      </Box>

      <Box sx={styles.body}>
        <Typography sx={styles.title}>{suggestion.title}</Typography>
        <Typography sx={styles.rationale}>{suggestion.body}</Typography>

        <Box sx={styles.rawUrl}>
          <Box
            component="i"
            className="fa-regular fa-globe"
            sx={styles.rawUrlIcon}
          />
          <Box sx={styles.rawUrlText}>{suggestion.anchorText}</Box>
        </Box>

        <Typography sx={styles.fieldLabel}>Show this as</Typography>

        <Box sx={styles.inputWrapper}>
          <Box
            component="i"
            className="fa-solid fa-font"
            sx={styles.inputIcon}
          />
          <Box
            component="input"
            type="text"
            value={anchorText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAnchorText(e.target.value)
            }
            placeholder="e.g. my media kit"
            sx={styles.input}
          />
        </Box>

        <Box sx={styles.preview}>
          Preview:{" "}
          <Box
            component="span"
            sx={styles.previewHtml}
            dangerouslySetInnerHTML={{
              __html: anchorText || suggestion.proposedReplacement || "",
            }}
          />
        </Box>
      </Box>

      <Box sx={styles.actions}>
        <ButtonBase sx={styles.primaryButton} onClick={handleApply}>
          <Box
            component="i"
            className="fa-solid fa-check"
            sx={styles.primaryIcon}
          />
          Hyperlink it
        </ButtonBase>
        <ButtonBase sx={styles.rejectButton} onClick={onDismiss}>
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={styles.rejectIcon}
          />
        </ButtonBase>
        <ButtonBase sx={styles.whyButton}>
          <Box
            component="i"
            className="fa-regular fa-circle-info"
            sx={styles.whyIcon}
          />
          Why?
        </ButtonBase>
      </Box>
    </Box>
  );
};

export default HyperlinkSuggestionPopup;
